import { useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BusinessSettingsLayout } from '@/components/business-settings/BusinessSettingsLayout';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Bell, Mail, AlertTriangle, Info, ShieldCheck, ShieldOff } from 'lucide-react';
import { useCompanyNotificationPreferences, useUpdateCompanyPreference } from '@/hooks/notifications';
import { useMyProfile } from '@/hooks/profile';
import { NotificationDeliveryCard } from '@/components/notifications';
import { 
  NOTIFICATION_TYPES, 
  NOTIFICATION_TYPE_METADATA,
  type NotificationTypeKey,
  type NotificationCategory,
} from '@/constants/notificationTypes';

interface PreferenceState {
  enabled: boolean;
  channels: string[];
  force_enabled: boolean;
}

// Default preferences for each notification type
function getDefaultPreference(typeKey: NotificationTypeKey): PreferenceState {
  const meta = NOTIFICATION_TYPE_METADATA[typeKey];
  return {
    enabled: true,
    channels: meta?.defaultChannels || ['in_app'],
    force_enabled: false,
  };
}

// Group notification types by category
function groupByCategory(types: NotificationTypeKey[]): Record<NotificationCategory, NotificationTypeKey[]> {
  const groups: Record<string, NotificationTypeKey[]> = {};
  
  for (const type of types) {
    const category = type.split('.')[0] as NotificationCategory;
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(type);
  }
  
  return groups as Record<NotificationCategory, NotificationTypeKey[]>;
}

// Category display names and icons
const CATEGORY_INFO: Record<NotificationCategory, { label: string; icon: React.ReactNode; description: string }> = {
  attendance: { 
    label: 'Attendance', 
    icon: <AlertTriangle className="h-5 w-5" />,
    description: 'Missed punches, late arrivals, early departures, unscheduled and out-of-area check-ins',
  },
  timesheet: { 
    label: 'Timesheets', 
    icon: <Info className="h-5 w-5" />,
    description: 'Timesheet approvals and rejections',
  },
  shift: { 
    label: 'Shifts', 
    icon: <Bell className="h-5 w-5" />,
    description: 'Shift swap requests and status changes',
  },
  leave: { 
    label: 'Leave', 
    icon: <Bell className="h-5 w-5" />,
    description: 'Leave request submissions and status changes',
  },
};

// Get human-readable label for a notification type
function getTypeLabel(typeKey: NotificationTypeKey): string {
  const meta = NOTIFICATION_TYPE_METADATA[typeKey];
  if (meta?.description) return meta.description;
  
  // Fallback: convert type key to readable format
  const event = typeKey.split('.')[1];
  return event.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

export default function CommunicationsSettings() {
  const { data: profile } = useMyProfile();
  const companyId = profile?.company_id;
  
  const { data: savedPreferences, isLoading, isError } = useCompanyNotificationPreferences(companyId);
  const { mutate: updatePreference, isPending } = useUpdateCompanyPreference(companyId);
  
  // Derive preference state from saved data (React Query cache is source of truth)
  const preferences = useMemo(() => {
    const allTypes = Object.values(NOTIFICATION_TYPES) as NotificationTypeKey[];
    const state: Record<string, PreferenceState> = {};
    
    for (const typeKey of allTypes) {
      const saved = savedPreferences?.find(p => p.type_key === typeKey);
      if (saved) {
        state[typeKey] = {
          enabled: saved.enabled,
          channels: saved.channels,
          force_enabled: saved.force_enabled,
        };
      } else {
        state[typeKey] = getDefaultPreference(typeKey);
      }
    }
    
    return state;
  }, [savedPreferences]);
  
  // Group types by category
  const groupedTypes = useMemo(() => {
    const allTypes = Object.values(NOTIFICATION_TYPES) as NotificationTypeKey[];
    return groupByCategory(allTypes);
  }, []);
  
  // Handle preference toggle (main switch) - auto-saves
  // Preserves channel selections when toggling off/on
  const handleToggle = (typeKey: string, enabled: boolean) => {
    const current = preferences[typeKey];
    const currentChannels = current?.channels || [];
    
    updatePreference({
      type_key: typeKey,
      enabled,
      // Preserve channels when disabling; restore (or default) when enabling
      channels: enabled 
        ? (currentChannels.length > 0 ? currentChannels : ['in_app'])
        : currentChannels,
    });
  };
  
  // Toggle channel for a preference - auto-saves
  const handleChannelToggle = (typeKey: string, channel: string) => {
    const current = preferences[typeKey];
    if (!current) return;
    
    // Block removal of last channel for force-enabled notifications
    if (current.force_enabled && current.channels.includes(channel) && current.channels.length === 1) {
      return;
    }
    
    const hasChannel = current.channels.includes(channel);
    const newChannels = hasChannel
      ? current.channels.filter(c => c !== channel)
      : [...current.channels, channel];
    
    // Derive enabled state from channels: empty = disabled
    const newEnabled = newChannels.length > 0;
    
    updatePreference({
      type_key: typeKey,
      enabled: newEnabled,
      channels: newChannels,
    });
  };
  
  // Toggle force-enable for a notification type - auto-saves
  const handleForceEnableToggle = (typeKey: string, forceEnabled: boolean) => {
    const current = preferences[typeKey];
    if (!current) return;
    
    // When force-enabling, ensure the notification is also enabled with at least one channel
    const updates: { type_key: string; force_enabled: boolean; enabled?: boolean; channels?: string[] } = {
      type_key: typeKey,
      force_enabled: forceEnabled,
    };
    
    if (forceEnabled) {
      updates.enabled = true;
      // Ensure at least one channel exists
      if (current.channels.length === 0) {
        updates.channels = ['in_app'];
      }
    }
    
    updatePreference(updates);
  };
  
  if (isLoading) {
    return (
      <BusinessSettingsLayout
        title="Communications"
        description="Configure notifications, messaging, and communication preferences"
      >
        <div className="space-y-6">
          {[1, 2, 3].map(i => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-4 w-72 mt-2" />
              </CardHeader>
              <CardContent className="space-y-4">
                {[1, 2].map(j => (
                  <Skeleton key={j} className="h-16 w-full" />
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      </BusinessSettingsLayout>
    );
  }
  
  if (isError) {
    return (
      <BusinessSettingsLayout
        title="Communications"
        description="Configure notifications, messaging, and communication preferences"
      >
        <Card>
          <CardContent className="py-8">
            <div className="text-center text-muted-foreground">
              <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-destructive" />
              <p>Failed to load notification preferences.</p>
              <p className="text-sm mt-1">Please try refreshing the page.</p>
            </div>
          </CardContent>
        </Card>
      </BusinessSettingsLayout>
    );
  }
  
  return (
    <BusinessSettingsLayout
      title="Communications"
      description="Configure notifications, messaging, and communication preferences"
    >
      <div className="space-y-6">
        {/* Notification categories */}
        {(Object.entries(groupedTypes) as [NotificationCategory, NotificationTypeKey[]][]).map(([category, types]) => {
          const categoryInfo = CATEGORY_INFO[category];
          
          return (
            <Card key={category}>
              <CardHeader>
                <div className="flex items-center gap-2">
                  {categoryInfo.icon}
                  <CardTitle>{categoryInfo.label} Notifications</CardTitle>
                </div>
                <CardDescription>{categoryInfo.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {types.map(typeKey => {
                  const pref = preferences[typeKey];
                  if (!pref) return null;
                  
                  const meta = NOTIFICATION_TYPE_METADATA[typeKey];
                  
                  return (
                    <div
                      key={typeKey}
                      className={`flex items-start justify-between gap-4 p-4 rounded-lg border bg-card transition-opacity ${
                        pref.channels.length === 0 ? 'opacity-60' : ''
                      }`}
                    >
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{getTypeLabel(typeKey)}</span>
                        </div>
                        
                        {/* Channel selection and Force Enable toggle */}
                        <div className="flex items-center gap-4 pt-2">
                          <label className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Checkbox
                              checked={pref.channels.includes('in_app')}
                              onCheckedChange={() => handleChannelToggle(typeKey, 'in_app')}
                              disabled={isPending || (pref.force_enabled && pref.channels.length === 1 && pref.channels.includes('in_app'))}
                            />
                            <Bell className="h-4 w-4" />
                            In-app
                          </label>
                          <label className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Checkbox
                              checked={pref.channels.includes('email')}
                              onCheckedChange={() => handleChannelToggle(typeKey, 'email')}
                              disabled={isPending || (pref.force_enabled && pref.channels.length === 1 && pref.channels.includes('email'))}
                            />
                            <Mail className="h-4 w-4" />
                            Email
                          </label>
                          
                          {/* Force Enable toggle */}
                          <div className="border-l pl-4 ml-2">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <label className={`flex items-center gap-2 text-sm cursor-pointer transition-colors ${
                                    pref.force_enabled 
                                      ? 'text-primary font-medium' 
                                      : 'text-muted-foreground hover:text-foreground'
                                  }`}>
                                    <Checkbox
                                      checked={pref.force_enabled}
                                      onCheckedChange={(checked) => handleForceEnableToggle(typeKey, checked === true)}
                                      disabled={isPending}
                                    />
                                    {pref.force_enabled ? (
                                      <ShieldCheck className="h-4 w-4" />
                                    ) : (
                                      <ShieldOff className="h-4 w-4" />
                                    )}
                                    Required
                                  </label>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-xs">
                                  <p className="text-sm">
                                    {pref.force_enabled 
                                      ? 'This notification is required. Users cannot disable it in their personal settings.'
                                      : 'Mark as required to prevent users from disabling this notification.'}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </div>
                      </div>
                      
                      {/* Enable/disable switch */}
                      <div className="flex flex-col items-end gap-1">
                        <Switch
                          checked={pref.enabled && pref.channels.length > 0}
                          onCheckedChange={(checked) => handleToggle(typeKey, checked)}
                          disabled={isPending || pref.force_enabled}
                        />
                        <span className="text-xs text-muted-foreground">
                          {pref.enabled && pref.channels.length > 0 ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          );
        })}
        
        {/* Delivery Status (Admin only) */}
        <NotificationDeliveryCard />
        
        {/* Info card */}
        <Card>
          <CardHeader>
            <CardTitle>About Notification Preferences</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground space-y-2">
            <p>
              These settings control the default notification behavior for your organization.
              Individual team members can override these settings in their personal preferences.
            </p>
            <p>
              <strong>In-app</strong> notifications appear in the notification center.
              <strong className="ml-1">Email</strong> notifications are sent to the user's email address.
            </p>
            <p>
              Notifications marked as <strong className="inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3 inline" />Required</strong> cannot 
              be disabled by individual users and are typically used for critical alerts.
            </p>
            <p className="text-xs italic pt-2">
              Changes are saved automatically.
            </p>
          </CardContent>
        </Card>
      </div>
    </BusinessSettingsLayout>
  );
}
